import { ActionsContext, ActionsContextInitialValues, ActionPlanMetadata, ElligibleStartingPost } from "./context";
import { Score } from "src/types";
import { ScoreState } from "../EntityBasedScore/useEntityBasedScoreParams";
import { ScoreService } from "../EntityBasedScore/ScoreService";
import { Ssq } from "src/api/sure-api/ssq/useGetOneSsq";
import { useSavingStatus } from "src/components/Loader/useSavingStatus";
import { useActionPlanData } from "./useActionPlanData";
import { useGetAllActionPlanActions } from "src/api/client-api/ActionPlan/ActionPlanAction/useGetAllActionPlanAction";
import { useGetOneAction } from "./useGetOneAction";
import { useActionScore } from "./useActionScore";
import { useState } from "react";
import { useCreateActionPlan } from "./useCreateActionPlan";
import { TrajectoryCreationModal } from "src/components/Actions/TrajectoryCreationModal/TrajectoryCreationModal";
import { useActionPlanOptions } from "./useActionPlanOptions";
import { Stack, Typography, useTheme } from "@mui/material";
import { FoodsIllustrations } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { Loading } from "src/components/utils/Loading";

export type ActionUrls = {
    noActionPlan: () => string;
    planning: () => string;
    planningList: () => string;
    planningPreview: () => string;
    trajectory: () => string;
    trajectoryBarChart: () => string;
    trajectoryCards: () => string;
    editPlanning: (actionId: string) => string;
    chart: (scoreId: number) => string;
};

type ActionsProviderProps = {
    children: JSX.Element;

    elligibleStartingPosts: ElligibleStartingPost[] | undefined;

    // Can delete SSQ pointer when backend provides us with the relevant information.
    ssq: Ssq;
    scoresEnabled: Score[];
    scoreState: ScoreState;
    extraFilterForAction: AxiosParams;
    urls: ActionUrls;
};
export const ActionsProvider = (props: ActionsProviderProps) => {
    const { scoresEnabled, ssq, urls, elligibleStartingPosts, extraFilterForAction } = props;

    const persistedOptions = useActionPlanOptions();
    const [isEditMode, setIsEditMode] = useState(false);
    const toggleEditMode = () => {
        setIsEditMode(!isEditMode);
    };

    const creationLoadingStatus = useSavingStatus();
    const { createActionPlan } = useCreateActionPlan({
        urls,
        setSavingStatus: creationLoadingStatus.setSaving,
    });

    const savingState = useSavingStatus();
    const [isOpen, setOpen] = useState(false);

    const { actionPlan, isFetched, isRefetching, scoreState, isReady } = useGetOneAction({
        params: extraFilterForAction,
    });
    const { data: allAPA } = useGetAllActionPlanActions({ actionPlanID: actionPlan?.id });

    const lastItem2 = (actionPlan?.simulationSsqPosts ?? []).length - 1;
    const postWithActionPlan = actionPlan?.simulationSsqPosts[lastItem2];

    const scoreList = ScoreService.getScoreList(
        scoresEnabled,
        postWithActionPlan?.scores || {},
        (scoreId, scoreVariationId) => {
            scoreState.selectVariation(scoreId, scoreVariationId);
        },
        scoreState.lastSelectedVariation,
    );

    const lastItem1 = (actionPlan?.actionPlanSsqPosts ?? []).length - 1;
    const postWithoutActionPlan = actionPlan?.actionPlanSsqPosts[lastItem1];

    const firstPostWithActionPlan = actionPlan?.simulationSsqPosts[0];
    const actionPlanMetadata: ActionPlanMetadata = {
        actionPlanID: actionPlan?.id,
        actionPlanIRI: actionPlan?.["@id"],
        startingPostId: actionPlan?.startSsqPost.id,
        targetPostIdWithoutActionPlan: postWithoutActionPlan?.ssqPost.id,

        firstPostIdWithActionPlan: firstPostWithActionPlan?.id,
        targetPostIdWithActionPlan: postWithActionPlan?.id,

        isGrowthDisabled: actionPlan?.simulationVaryPercentage === undefined,
    };

    const actionScore = useActionScore({ ssqId: ssq.id, scoreState, actionPlan, scoreList });
    const {
        getActionScoresByCsrTheme,
        actionsByCsrTheme,
        actions,
        selectPostsByActionPlanAction,
        selectPostsByCampaigns,
        getPropertiesByAction,
        isLoading: areActionsLoading,
    } = actionScore;

    const actionPlanData = useActionPlanData({ actionPlan, scores: scoreList.scoresWithVariations });

    const selectedOption = scoreList.lastSelected ?? scoreList.scoresWithVariations[0];
    const selectedActionPlan = actionPlanData.find((i) => {
        return i.scoreId === selectedOption.scoreId && i.variationId === selectedOption.variationId;
    });

    const startingCampaign = selectedActionPlan?.metadata.startingPost.campaign?.id;
    const endingCampaign = selectedActionPlan?.metadata.scoresWithActionPost.lastItem?.campaign?.id;

    const postsToCompare = selectPostsByCampaigns(selectedActionPlan, startingCampaign, endingCampaign, true);

    const allActivatedActions = allAPA?.map((apa) => apa.actionId) ?? [];
    const actionScores = getActionScoresByCsrTheme(postsToCompare, allActivatedActions);

    const actionPlanStats = {
        // Future elligible actions from actionPlan;
        total: (actions ?? []).length,
        engagedActions: (allAPA ?? []).length,
    };

    const items: ActionsContextInitialValues = {
        areActionsLoading,
        actionPlan: actionPlan ?? null,
        actionPlanData,
        selectPostsByActionPlanAction,
        selectPostsByCampaigns,
        actionPlanMetadata,
        actions,
        actionsByCsrTheme,
        selectedActionPlan,
        actionScores,
        getActionScoresByCsrTheme,
        allScoreWithVariations: scoreList.scoresWithVariations,
        elligibleStartingPosts,
        isFetched,
        isRefetching,
        getPropertiesByAction,
        savingStatus: savingState,
        actionPlanSavingStatus: creationLoadingStatus,
        scoresEnabled,
        scoreOptions: scoreState,
        selectedScore: scoreList.lastSelected,
        actionPlanStats,
        urls,
        setOpenInitForm: setOpen,
        isEditMode,
        toggleEditMode,
        persistedOptions,
    };

    if (isReady === false) {
        return <Loading />;
    }

    return (
        <ActionsContext.Provider value={items}>
            <TrajectoryCreationModal
                isOpen={isOpen}
                setOpen={setOpen}
                createActionPlan={createActionPlan}
                elligibleStartingPosts={elligibleStartingPosts}
                actionPlan={actionPlan ?? null}
                scoresEnabled={scoresEnabled}
            />
            {props.children}
        </ActionsContext.Provider>
    );
};
